import {useContext, useState} from 'react';
import * as React from 'react';
import {StoreContext} from "../../stores/StoreLoader";
import {capitalize} from "../../utils/StringUtilities";
import FontAwesome from "../utilities/FontAwesome";
import {observer} from "mobx-react";
import {YiiUrlFactory} from "../../backends/YiiRoutes";
import {sb_hideAllAdminNavModals} from "../../utils/SchoolBlocks";
import URI from 'urijs';
import {withoutLocaleInURI} from "../../internationalization/i18nURI";
import NotificationManager from "../notifications/NotificationManager";

const AdminMenu: React.FC<{}> = observer(props => {
    const {i18nStore, organizationStore, userStore, modalStore, gridStore, interfaceStore} = useContext(StoreContext);
    const {adminAccount} = organizationStore.organization.json_data.settings.identity.google;
    const [activeModal, setActiveModal] = useState<string | null>(null);

    let pageType = organizationStore.currentOrganization.type;
    let manageText, deleteText, deleteUrl, addText, showFluxCapacitor;

    const createTexts: Array<{ text: string, pageType: string }> = [];
    if (organizationStore.currentOrganization.type === 'collection') {
        const rules = organizationStore.currentOrganization.collectionRules;
        rules.forEach(rule => {
            if (rule.collection_of === 'organizations' && rule.type === 'type') {
                rule.rule.forEach(type => {
                    if (type !== "school") {
                        createTexts.push({
                            text: "Create " + capitalize(type),
                            pageType: type,
                        })
                    }
                })
            } else if (rule.collection_of === "people") {
                pageType = "peopleCollection";
            }
        })
    }

    const pathname = URI(withoutLocaleInURI(interfaceStore.currentFullUrl)).path();
    if (pathname === "/calendar") {
        pageType = "calendar";
    }

    switch (pageType) {
        case "peopleCollection":
        case "collection":
            manageText = 'Manage Members';
            break;
        case "page":
            deleteText = 'Delete Page';
            deleteUrl = YiiUrlFactory(`/cms/page/delete?id=${organizationStore.currentOrganization.id}`, i18nStore.currentOrgSupportedLocale);
            break;
        case "group":
        case "department":
        case "section":
        case "team":
            addText = <FontAwesome name="fas fa-plus-circle" aria-label="Add Content"/>;
            deleteText = 'Delete ' + capitalize(organizationStore.currentOrganization.type);
            deleteUrl = YiiUrlFactory(`/cms/section/softDelete?id=${organizationStore.currentOrganization.id}`, i18nStore.currentOrgSupportedLocale);
            showFluxCapacitor = true;
            break;
        case "classroom":
            addText = <FontAwesome name="fas fa-plus-circle" aria-label="Add Content"/>;
            deleteText = 'Delete Classroom';
            deleteUrl = YiiUrlFactory(`/cms/classroom/softDelete?id=${organizationStore.currentOrganization.id}`, i18nStore.currentOrgSupportedLocale);
            showFluxCapacitor = true;
            break;
        case "calendar":
            manageText = 'Manage Calendars';
            break;
        default:
            addText = <FontAwesome name="fas fa-plus-circle" aria-label="Add Content"/>;
            showFluxCapacitor = true;
    }

    function hideAllModalsExcept(except: string, modalObj?: any) {
        modalStore.clearAllModals();
        sb_hideAllAdminNavModals(except);
        if (modalObj && activeModal !== modalObj.type) {
            modalStore.addModal(modalObj);
        }
        setActiveModal(null);
    }

    return <div className="sb-admin-nav">
        <nav className="sb-admin-main-nav" aria-label="Administrative Menu">
            <ul>
                {manageText && <li key="manage" title={manageText}>
                    <button className={`sb-admin-menu-manage-${pageType} btn-icon`}
                            onClick={() => {
                                if (organizationStore.currentOrganization.type === "collection") {
                                    hideAllModalsExcept("", {
                                        id: pageType === "peopleCollection" ? 'people-collection-management-modal' : 'collection-management-modal',
                                        type: pageType === "peopleCollection" ? 'peopleCollectionManagement' : 'collectionManagement',
                                        title: 'Manage Members',
                                        itemObj: {},
                                        gridId: gridStore.id,
                                    })
                                } else if (pageType === "calendar") {
                                    hideAllModalsExcept("", {
                                        type: "calendarManagement",
                                    })
                                }
                            }}
                            title={manageText}>
                        <FontAwesome name="fas fa-cog" aria-label={manageText}/>
                    </button>
                </li>}
                {createTexts.map(info => <li key={"add" + info.text} title={info.text}>
                    <button className="btn-icon"
                            title={info.text}
                            aria-label={info.text}
                            onClick={() => {
                                hideAllModalsExcept("", {
                                    type: "addOrEditBlock",
                                    blockType: info.pageType,
                                    intent: "add",
                                });
                            }}>
                        <FontAwesome name="fas fa-plus-circle" aria-label={info.text}/>
                    </button>
                </li>)}
                {addText && <li key="add" className={activeModal === "toolDrawerGrid" ? "active" : ""}>
                    <button id="btn-trigger-add-block" className="btn-icon"
                            title="Add content"
                            aria-label="Add content"
                            onClick={function () {
                                if (gridStore.blocks.size > 11) {
                                    NotificationManager.warning(
                                        "Can we help you improve page layout with fewer blocks? Contact support" +
                                        " to discuss further!",
                                        "Hmmm, that's a lot of blocks.",
                                        5000,
                                    )
                                }
                                hideAllModalsExcept("", {
                                    type: "toolDrawerGrid",
                                    setActiveModal,
                                })
                            }}>
                        {addText}
                    </button>
                    <span className="curvetop" role="presentation" aria-hidden="true"/>
                    <span className="curvebottom" role="presentation" aria-hidden="true"/>
                </li>}
                {adminAccount !== null && pageType === 'classroom' && <li key="linkExternalClassroom" >
                    <button className="btn-icon"
                            aria-label="Link to Google Classroom"
                            onClick={() => {
                                hideAllModalsExcept("", {
                                    type: "linkExternalClassroom",
                                });
                            }}>
                        <img src={require("../../assets/images/google_classroom_green.png")}
                             width="26px" style={{paddingTop: "3px"}} aria-hidden="true"
                             alt="Link to Google Classroom"/>
                    </button>
                </li>}
                {pageType === 'page' && <>
                    <li key="editPage">
                        <button className="btn-icon"
                                title="Edit page"
                                aria-label="Edit page"
                                onClick={() => {
                                    hideAllModalsExcept("editPage");
                                    interfaceStore.toggleEditingPage();
                                }}>
                            <FontAwesome name="fas fa-pencil-alt" aria-label="Edit page"/>
                        </button>
                    </li>
                    <li key="compareVersions">
                        <button className="btn-icon"
                                title="Compare versions"
                                aria-label="Compare versions"
                                onClick={() => {
                                    hideAllModalsExcept("", {
                                        id: "comparePagesModal",
                                        type: "comparePages",
                                    })
                                }}>
                            <FontAwesome name="fas fa-undo" aria-label="compare versions"/>
                        </button>
                    </li>
                </>}
                {pageType !== 'collection' && pageType !== "peopleCollection" && userStore.isAdmin &&
                    <li key="permissions">
                        <button className="btn-icon"
                                title="Manage permissions"
                                aria-label="Manage permissions"
                                onClick={() => {
                                hideAllModalsExcept("", {
                                    type: "permissions",
                                });
                                }}>
                            <FontAwesome name="fas fa-users" aria-label="Manage permissions"/>
                        </button>
                    </li>}
                {showFluxCapacitor && <li key="flux">
                    <button title="Flux Capacitor" className="btn-icon"
                            onClick={() => {
                                hideAllModalsExcept("", {
                                    type: "fluxCapacitor",
                                });
                            }}
                            aria-label="Open Flux Capacitor">
                        <img alt="Open Flux Capacitor" src={require("../../assets/images/flux-capacitor.svg") as string}
                             width="32px"/>
                    </button>
                </li>}
                <li key="help" className={activeModal === "helpModal" ? "active" : ""}>
                    <button id="btn-trigger-help-window" role="button" className="btn-icon"
                            onClick={() => {
                                hideAllModalsExcept("", {
                                    type: "helpModal",
                                    setActiveModal,
                                });
                            }}
                            aria-label="Need help?"
                            title="Need help?">
                        <FontAwesome prefix={"fas"} name="fa-question-circle" aria-label="Need help?"/>
                    </button>
                    <span className="curvetop" role="presentation" aria-hidden="true"/>
                    <span className="curvebottom" role="presentation" aria-hidden="true"/>
                </li>
                {deleteUrl && deleteText && (organizationStore.currentOrganization.deleted ? <li>
                    <button
                        onClick={() => modalStore.addModal({
                            type: "confirm",
                            orgType: organizationStore.currentOrganization.type === "department" ? "group" : organizationStore.currentOrganization.type,
                            orgID: organizationStore.currentOrganization.id,
                            message: `Are you sure you want to restore this ${organizationStore.currentOrganization.type}?`,
                        })}
                        className={"btn-icon"}
                        aria-label={"Restore"}
                        title={"Restore"}
                    >
                        <FontAwesome name="fas fa-trash-restore" ariaHidden={true}/>
                    </button>
                </li> : <li key="delete">
                    <button className="btn-icon"
                            title={deleteText}
                            aria-label={deleteText}
                            onClick={() => {
                                hideAllModalsExcept("", {
                                    type: "deleteDialog",
                                    deleteUrl,
                                });
                            }}>
                        <FontAwesome name="fas fa-trash-alt" aria-label={deleteText}/></button>
                </li>)}
            </ul>
        </nav>
    </div>
});

export default AdminMenu;
